import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SendIcon from "@mui/icons-material/Send";
import {
  SectionPanel,
  CodeChip,
  StatusPill,
  ClinicalEmptyState,
  fmtMoney,
  tokens,
} from "../../../shared/clinical-design";
import { CHARGE_STATUS } from "../../common/constants";
import * as cdci from "../../services/cdciAmdService";

// Sugerencias de E/M por tipo de cita (heurística local hasta que exista el
// Charge Slip Recommendation Engine del Módulo N — N.1).
const SUGGESTED_CPT_BY_TYPE = {
  AWV: ["G0439", "96127"],
  FOLLOW_UP: ["99214"],
  SICK: ["99213"],
  TELEHEALTH: ["99443"],
  HOSP_FU: ["99496", "1111F"],
};

/**
 * CDCI 4.4 — Captura de Cargos (Paso 5 del journey).
 * Workflow oficial AdvancedMD: lookupproccode (ID interno) → savecharges →
 * verificación. Los CPT van por ID interno; los ICD por código.
 * Fuente futura: GET api/amd/charges/proc-codes · diag-codes · POST api/amd/charges.
 */
export default function ChargesTab({ appointment, patient, note, charges, onChargesChanged, readOnly }) {
  const [procQuery, setProcQuery] = useState("");
  const [procResults, setProcResults] = useState([]);
  const [diagQuery, setDiagQuery] = useState("");
  const [diagResults, setDiagResults] = useState([]);
  const [procedures, setProcedures] = useState(charges?.procedures || []);
  const [diagnoses, setDiagnoses] = useState(charges?.diagnoses || []);
  const [saving, setSaving] = useState(false);
  const [allProcs, setAllProcs] = useState([]);

  const captured = charges?.status === CHARGE_STATUS.CAPTURED.key;

  useEffect(() => {
    cdci.lookupProcCodes("").then(setAllProcs);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (procQuery) cdci.lookupProcCodes(procQuery).then(setProcResults);
      else setProcResults([]);
    }, 250);
    return () => clearTimeout(t);
  }, [procQuery]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (diagQuery) cdci.lookupDiagCodes(diagQuery).then(setDiagResults);
      else setDiagResults([]);
    }, 250);
    return () => clearTimeout(t);
  }, [diagQuery]);

  const suggested = useMemo(() => {
    const cpts = SUGGESTED_CPT_BY_TYPE[appointment?.typeKey] || [];
    return allProcs.filter((p) => cpts.includes(p.cpt) && !procedures.some((x) => x.procId === p.procId));
  }, [allProcs, appointment, procedures]);

  const addProcedure = (p) => {
    if (procedures.some((x) => x.procId === p.procId)) return;
    setProcedures((prev) => [...prev, { ...p, units: 1, modifiers: "" }]);
    setProcQuery("");
    setProcResults([]);
  };

  const addDiagnosis = (d) => {
    if (diagnoses.some((x) => x.code === d.code)) return;
    setDiagnoses((prev) => [...prev, d]);
    setDiagQuery("");
    setDiagResults([]);
  };

  // Problemas activos del paciente como quick-add de diagnósticos
  const quickDx = useMemo(() => {
    return (patient?.activeProblems || [])
      .map((p) => {
        const [code, ...rest] = p.split(" — ");
        return { code: code.trim(), description: rest.join(" — "), hcc: null };
      })
      .filter((d) => !diagnoses.some((x) => x.code === d.code));
  }, [patient, diagnoses]);

  // --- Validaciones previas al envío (anticipo del N.2 Validation Layer) ----
  const validations = useMemo(() => {
    const checks = [];
    checks.push({
      label: "At least one procedure (CPT)",
      pass: procedures.length > 0,
    });
    checks.push({
      label: "At least one diagnosis (ICD-10)",
      pass: diagnoses.length > 0,
    });
    checks.push({
      label: "Encounter note started",
      pass: Boolean(note),
    });
    checks.push({
      label: "Place of service on file",
      pass: Boolean(appointment?.location?.pos),
    });
    return checks;
  }, [procedures, diagnoses, note, appointment]);

  const allPass = validations.every((v) => v.pass);
  const total = procedures.reduce((acc, p) => acc + (p.fee || 0) * (p.units || 1), 0);

  const persist = async (asDraft) => {
    setSaving(true);
    try {
      const payload = { procedures, diagnoses };
      const result = asDraft
        ? await cdci.draftCharges(appointment.appointmentId, payload)
        : await cdci.saveCharges(appointment.appointmentId, payload);
      onChargesChanged(result);
    } finally {
      setSaving(false);
    }
  };

  const SearchResults = ({ items, onPick, renderLabel }) =>
    items.length > 0 && (
      <Box sx={{ border: `1px solid ${tokens.line}`, borderRadius: "4px", mt: 0.5, maxHeight: 180, overflowY: "auto" }}>
        {items.map((item) => (
          <Box
            key={item.procId || item.code}
            onClick={() => onPick(item)}
            sx={{
              px: 1.2, py: 0.6, cursor: "pointer", fontSize: 13,
              "&:hover": { bgcolor: tokens.primary50 },
              borderBottom: `1px solid ${tokens.line}`,
              "&:last-child": { borderBottom: 0 },
            }}
          >
            {renderLabel(item)}
          </Box>
        ))}
      </Box>
    );

  return (
    <Grid container spacing={1.5}>
      {/* Columna izquierda: construcción del charge slip */}
      <Grid size={{ xs: 12, lg: 7 }}>
        <Stack spacing={1.5}>
          {captured && (
            <Box sx={{ bgcolor: tokens.okBg, border: `1px solid ${tokens.ok}`, borderRadius: "6px", px: 2, py: 1 }}>
              <Typography sx={{ fontSize: 13, color: tokens.ok, fontWeight: 600 }}>
                Charges captured and sent to AdvancedMD on {new Date(charges.savedAt).toLocaleString()}.
              </Typography>
            </Box>
          )}

          <SectionPanel title="Procedures (CPT)">
            {!readOnly && !captured && (
              <>
                {suggested.length > 0 && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap", rowGap: 0.8 }}>
                    <Typography sx={{ fontSize: 12, color: tokens.ink3 }}>Suggested for this visit type:</Typography>
                    {suggested.map((p) => (
                      <Tooltip key={p.procId} title={p.description} arrow>
                        <Button
                          size="small"
                          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                          onClick={() => addProcedure(p)}
                          sx={{ textTransform: "none", fontFamily: tokens.fontMono, fontSize: 12, border: `1px dashed ${tokens.info}`, color: tokens.info, py: 0 }}
                        >
                          {p.cpt}
                        </Button>
                      </Tooltip>
                    ))}
                  </Stack>
                )}
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search CPT by code or description (lookupproccode)…"
                  value={procQuery}
                  onChange={(e) => setProcQuery(e.target.value)}
                  sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
                />
                <SearchResults
                  items={procResults}
                  onPick={addProcedure}
                  renderLabel={(p) => (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CodeChip code={p.cpt} />
                      <Typography sx={{ fontSize: 13 }}>{p.description}</Typography>
                      <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.ink3, ml: "auto" }}>
                        {fmtMoney(p.fee)}
                      </Typography>
                    </Stack>
                  )}
                />
              </>
            )}

            {procedures.length === 0 ? (
              <ClinicalEmptyState message="No procedures on this charge slip yet." sx={{ py: 1.5 }} />
            ) : (
              <Box sx={{ mt: 1 }}>
                {procedures.map((p) => (
                  <Stack
                    key={p.procId}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ py: 0.6, borderBottom: `1px solid ${tokens.line}`, "&:last-child": { borderBottom: 0 } }}
                  >
                    <CodeChip code={p.cpt} raw={`procId ${p.procId}`} tone="info" />
                    <Typography sx={{ fontSize: 13, flex: 1 }}>{p.description}</Typography>
                    <TextField
                      size="small" type="number" label="Units" value={p.units}
                      disabled={readOnly || captured}
                      onChange={(e) =>
                        setProcedures((prev) =>
                          prev.map((x) => (x.procId === p.procId ? { ...x, units: Number(e.target.value) || 1 } : x))
                        )
                      }
                      sx={{ width: 72, "& .MuiInputBase-input": { fontSize: 13 } }}
                    />
                    <TextField
                      size="small" label="Mod" value={p.modifiers} placeholder="25"
                      disabled={readOnly || captured}
                      onChange={(e) =>
                        setProcedures((prev) =>
                          prev.map((x) => (x.procId === p.procId ? { ...x, modifiers: e.target.value } : x))
                        )
                      }
                      sx={{ width: 72, "& .MuiInputBase-input": { fontSize: 13, fontFamily: tokens.fontMono } }}
                    />
                    <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 13, width: 70, textAlign: "right" }}>
                      {fmtMoney((p.fee || 0) * (p.units || 1))}
                    </Typography>
                    {!readOnly && !captured && (
                      <IconButton size="small" onClick={() => setProcedures((prev) => prev.filter((x) => x.procId !== p.procId))}>
                        <DeleteOutlineIcon sx={{ fontSize: 17, color: tokens.crit }} />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Box>
            )}
          </SectionPanel>

          <SectionPanel title="Diagnoses (ICD-10)">
            {!readOnly && !captured && (
              <>
                {quickDx.length > 0 && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: "wrap", rowGap: 0.8 }}>
                    <Typography sx={{ fontSize: 12, color: tokens.ink3 }}>From active problems:</Typography>
                    {quickDx.map((d) => (
                      <Tooltip key={d.code} title={d.description} arrow>
                        <Button
                          size="small"
                          startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                          onClick={() => addDiagnosis(d)}
                          sx={{ textTransform: "none", fontFamily: tokens.fontMono, fontSize: 12, border: `1px dashed ${tokens.primary}`, color: tokens.primary, py: 0 }}
                        >
                          {d.code}
                        </Button>
                      </Tooltip>
                    ))}
                  </Stack>
                )}
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search ICD-10 by code or description (lookupdiagcode)…"
                  value={diagQuery}
                  onChange={(e) => setDiagQuery(e.target.value)}
                  sx={{ "& .MuiInputBase-input": { fontSize: 13 } }}
                />
                <SearchResults
                  items={diagResults}
                  onPick={addDiagnosis}
                  renderLabel={(d) => (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CodeChip code={d.code} />
                      <Typography sx={{ fontSize: 13 }}>{d.description}</Typography>
                      {d.hcc && <CodeChip code={d.hcc} tone="warn" />}
                    </Stack>
                  )}
                />
              </>
            )}
            {diagnoses.length === 0 ? (
              <ClinicalEmptyState message="No diagnoses linked yet." sx={{ py: 1.5 }} />
            ) : (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}>
                {diagnoses.map((d) => (
                  <Stack key={d.code} direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title={d.description} arrow>
                      <span><CodeChip code={d.code} tone="ok" /></span>
                    </Tooltip>
                    {d.hcc && <CodeChip code={d.hcc} tone="warn" />}
                    {!readOnly && !captured && (
                      <IconButton size="small" onClick={() => setDiagnoses((prev) => prev.filter((x) => x.code !== d.code))}>
                        <DeleteOutlineIcon sx={{ fontSize: 15, color: tokens.crit }} />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </SectionPanel>
        </Stack>
      </Grid>

      {/* Columna derecha: validación + resumen + envío */}
      <Grid size={{ xs: 12, lg: 5 }}>
        <Stack spacing={1.5}>
          <SectionPanel title="Pre-submit validation">
            {validations.map((v) => (
              <Stack key={v.label} direction="row" spacing={1} alignItems="center" sx={{ py: 0.4 }}>
                <StatusPill tone={v.pass ? "ok" : "warn"} label={v.pass ? "Pass" : "Pending"} dense />
                <Typography sx={{ fontSize: 13 }}>{v.label}</Typography>
              </Stack>
            ))}
          </SectionPanel>

          <SectionPanel title="Charge slip summary">
            <Stack direction="row" justifyContent="space-between" sx={{ py: 0.3 }}>
              <Typography sx={{ fontSize: 13, color: tokens.ink2 }}>Procedures</Typography>
              <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 13 }}>{procedures.length}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ py: 0.3 }}>
              <Typography sx={{ fontSize: 13, color: tokens.ink2 }}>Diagnoses</Typography>
              <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 13 }}>{diagnoses.length}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ py: 0.3, borderTop: `1px solid ${tokens.line}`, mt: 0.5, pt: 0.8 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Estimated total</Typography>
              <Typography sx={{ fontFamily: tokens.fontMono, fontSize: 14, fontWeight: 600 }}>{fmtMoney(total)}</Typography>
            </Stack>

            {!readOnly && !captured && (
              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={saving || procedures.length === 0}
                  onClick={() => persist(true)}
                  sx={{ textTransform: "none", borderColor: tokens.ink3, color: tokens.ink2 }}
                >
                  Save draft
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SendIcon sx={{ fontSize: 15 }} />}
                  disabled={!allPass || saving}
                  onClick={() => persist(false)}
                  sx={{ bgcolor: tokens.primary, "&:hover": { bgcolor: tokens.primary700 }, textTransform: "none", fontWeight: 600 }}
                >
                  {saving ? "Sending…" : "Capture charges"}
                </Button>
              </Stack>
            )}
          </SectionPanel>

          <Typography sx={{ fontSize: 11.5, color: tokens.ink3, px: 0.5 }}>
            savecharges runs on AdvancedMD Tier 2 (12/min peak). CPT codes are sent by
            internal procedure ID resolved via lookupproccode; ICD-10 codes are sent by code.
          </Typography>
        </Stack>
      </Grid>
    </Grid>
  );
}
